// Re-synced with LC2's current cookies.js (post "Security: use bearer service token for
// loyalty-cloud API" / "Auth: persist service_token from verify_login response"). LC2's
// old LCAPIKey-header auth for loyaltyCloudApi has been replaced with a short-lived
// `lc_service_token` JWT (cookie) sent as `Authorization: Bearer <token>` — see
// features/loyaltyCloudApi/apiSlice.js.
import Cookies from 'universal-cookie';

let cookies = new Cookies();

export const createNewCookie = (key, value) => {
    // Historically this function cleared all cookies before setting a new session.
    // Keep behavior for session_token, but avoid using it for additional auth tokens.
    removeAllCookies();
    return setCookie(key, value);
};

export const setCookie = (key, value, {expires} = {}) => {
    const now = new Date();
    const expirationDate = expires || new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

    try {
        cookies.set(key, value, {
            path: '/',
            expires: expirationDate,
            secure: window.location.protocol === 'https:',
            sameSite: 'lax'
        });

        const verifiedValue = cookies.get(key);

        if (verifiedValue !== value) {
            setTimeout(() => {
                const delayedVerifiedValue = cookies.get(key);
                if (delayedVerifiedValue !== value) {
                    cookies.set(key, value, {
                        path: '/',
                        expires: expirationDate,
                        secure: window.location.protocol === 'https:',
                        sameSite: 'lax'
                    });
                }
            }, 100);
        }
    } catch (error) {
        console.error('Error setting cookie:', error);
    }
};

export const getCookie = (name) => {
    return cookies.get(name);
};

// RFC 6265 compliant cookie name validation
const isValidCookieName = (name) => {
    if (!name || typeof name !== 'string') return false;
    const trimmed = name.trim();
    if (trimmed.length === 0) return false;
    return /^[!#$%&'*+\-.0-9A-Z^_`a-z|~]+$/.test(trimmed);
};

export const removeCookie = (name) => {
    try {
        if (isValidCookieName(name)) {
            cookies.remove(name, {path: '/'});
        } else {
            console.warn('Skipping invalid cookie name:', name, typeof name);
        }
    } catch (error) {
        console.error('Error removing cookie:', name, error);
    }
};

export const removeAllCookies = () => {
    try {
        const allCookies = cookies.getAll();
        Object.keys(allCookies).forEach((cookie) => {
            try {
                if (isValidCookieName(cookie)) {
                    cookies.remove(cookie, {path: '/'});
                } else {
                    console.warn('removeAllCookies: Skipping invalid cookie name:', cookie, 'type:', typeof cookie);
                }
            } catch (error) {
                console.error('removeAllCookies: Error removing cookie:', cookie, error);
            }
        });
    } catch (error) {
        console.error('removeAllCookies: Error getting all cookies:', error);
    }
};

const dec2hex = (dec) => {
    return dec.toString(16).padStart(2, '0');
};

export const generateId = (len) => {
    const arr = new Uint8Array((len || 40) / 2);
    window.crypto.getRandomValues(arr);
    return Array.from(arr, dec2hex).join('');
};

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const getSessionTokenWithRetry = async (retries = 5, interval = 1000) => {
    for (let i = 0; i < retries; i++) {
        const token = getCookie('session_token');
        if (token) return token;
        await delay(interval);
    }
    return null;
};

let currentToken = null;

export const initializeToken = async () => {
    currentToken = await getSessionTokenWithRetry();
    return currentToken;
};

export const getCurrentToken = () => {
    return currentToken;
};

export const clearCurrentToken = () => {
    currentToken = null;
};

export const refreshToken = async () => {
    clearCurrentToken();
    return await initializeToken();
};

// Loyalty Cloud v2: short-lived bearer token used for loyalty-cloud backend requests
// (replaces legacy LCAPIKey).
const LC_SERVICE_TOKEN_COOKIE = 'lc_service_token';

const _safeBase64UrlDecode = (s) => {
    try {
        const base64 = s.replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
        return atob(padded);
    } catch {
        return null;
    }
};

export const decodeJwtPayload = (jwt) => {
    if (!jwt || typeof jwt !== 'string') return null;
    const parts = jwt.split('.');
    if (parts.length < 2) return null;
    const json = _safeBase64UrlDecode(parts[1]);
    if (!json) return null;
    try {
        return JSON.parse(json);
    } catch {
        return null;
    }
};

export const isJwtExpired = (jwt, {skewSeconds = 30} = {}) => {
    const payload = decodeJwtPayload(jwt);
    const exp = payload?.exp;
    if (!exp) return true;
    const nowSec = Math.floor(Date.now() / 1000);
    return nowSec >= Number(exp) - skewSeconds;
};

export const setLcServiceTokenCookie = (token) => {
    // Keep this short-lived and refreshable. Set a conservative max cookie expiry.
    // If token is invalid/undecodable, fall back to a short (2h) cookie.
    const payload = decodeJwtPayload(token);
    const expSec = payload?.exp;
    const expires = expSec ? new Date(Number(expSec) * 1000) : new Date(Date.now() + 2 * 60 * 60 * 1000);
    return setCookie(LC_SERVICE_TOKEN_COOKIE, token, {expires});
};

export const getLcServiceTokenCookie = () => getCookie(LC_SERVICE_TOKEN_COOKIE);

export const clearLcServiceTokenCookie = () => removeCookie(LC_SERVICE_TOKEN_COOKIE);
